import React from "react";
import type { TeamAssistantRequest } from "../../model/AircraftStore";
import {
	getPilotRequestType,
	type NormalizedGoal,
} from "../../schemas/pilotRequestSchema";
import {
	findSuggestionForRequest,
	getRequestStatusColorClass,
	getStatusColorClass,
	isGoalPositive,
	isRejected,
} from "../../utils/teamAssistantHelper";

/** Traffic light status dot: green/yellow/red based on boolean/null/undefined. */
export function getStatusColor(status?: boolean | null) {
	if (status === undefined || status === null) {
		return <span className="text-yellow-500">●</span>;
	}
	return status ? (
		<span className="text-green-400">●</span>
	) : (
		<span className="text-red-500">●</span>
	);
}

/** Icon + status dot + parameter text (the left part of the header). */
export function TaHeaderContent({
	requestTypeIcon,
	requestParameter,
	request,
}: {
	requestTypeIcon: string;
	requestParameter: string;
	request: TeamAssistantRequest;
}) {
	return (
		<div className="flex justify-start gap-0.5">
			<img src={requestTypeIcon} alt="Request type" className="w-4 h-4" />
			<div className="flex items-center gap-0.5 text-xs">
				<span className={getRequestStatusColorClass(request)}>●</span>
				<span className="text-[#40c4ff]">{requestParameter}</span>
			</div>
		</div>
	);
}

/** X button to dismiss a request. */
export function DismissButton({ onClick }: { onClick: () => void }) {
	return (
		<span className="cursor-pointer border border-transparent hover:border-white">
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
				strokeWidth="1.5"
				stroke="currentColor"
				className="w-3 h-3"
				onClick={onClick}
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="M6 18 18 6M6 6l12 12"
				/>
			</svg>
		</span>
	);
}

/** Checkmark button to accept a request. */
export function AcceptCheckmark({ onClick }: { onClick: () => void }) {
	return (
		<span className="p-0.5 cursor-pointer border border-white border-opacity-70">
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
				strokeWidth="1.5"
				stroke="currentColor"
				className="w-3 h-3 inline-block cursor-pointer"
				onClick={onClick}
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="m4.5 12.75 6 6 9-13.5"
				/>
			</svg>
		</span>
	);
}

/** Suggestion status dot + suggestion text + optional accept checkmark. */
export function SuggestionContent({
	request,
	showAcceptCheckmark,
	onAccept,
}: {
	request: TeamAssistantRequest;
	showAcceptCheckmark: boolean;
	onAccept: () => void;
}) {
	const getXflInitDifferent = (): NormalizedGoal | false => {
		for (const goal of request.normalizedGoals) {
			if (
				isGoalPositive(
					goal,
					getPilotRequestType(request.context?.request_type ?? 0),
				)
			) {
				const initValue = goal.results?.initial_climb;
				const exitValue = goal.results?.exit_level;
				if (
					initValue !== undefined &&
					exitValue !== undefined &&
					initValue !== exitValue
				) {
					return goal;
				}
			}
		}
		return false;
	};
	const xflInitDifferent = getXflInitDifferent();
	return (
		<>
			{xflInitDifferent && (
				<div className="flex items-center justify-center gap-1">
					<span className={getStatusColorClass(1)}>●</span>
					<span className="text-xs text-[#40c4ff]">
						{`XFL ${xflInitDifferent.results?.exit_level}`} ?
					</span>
				</div>
			)}
			<div className="flex items-center justify-center gap-1 pb-1">
				<span className={getStatusColorClass(isRejected(request) ? 0 : 1)}>
					●
				</span>
				<span className="text-xs text-[#40c4ff]">
					{findSuggestionForRequest(request)}?
				</span>
				{showAcceptCheckmark && <AcceptCheckmark onClick={onAccept} />}
			</div>
		</>
	);
}

/** Expand arrow (down-right) to grow from small to full view. */
export function ExpandArrow({ onClick }: { onClick: () => void }) {
	return (
		<span className="cursor-pointer border border-transparent hover:border-transparent text-xs">
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
				strokeWidth="1.5"
				stroke="currentColor"
				className="w-3 h-3"
				onClick={onClick}
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="m4.5 4.5 15 15m0 0V8.25m0 11.25H8.25"
				/>
			</svg>
		</span>
	);
}

/** Level-change goal detail rows: TCT + FMP + Exit status. */
export function LevelChangeGoalRows({
	goal,
	index,
	totalGoals,
	isAP2,
	requestParameter,
}: {
	goal: NormalizedGoal;
	index: number;
	totalGoals: number;
	isAP2: boolean;
	requestParameter: number | string;
}) {
	if (!goal.results) {
		return null;
	}
	// if (!isAP2 && goal.requestedValue !== requestParameter) {
	// 	return null;
	// }

	// FMP status
	const fmpIsOk =
		goal.results.next_sector_capacity_ok && !goal.results.altitude_restriction;

	// Exit status color logic:
	// - yellow (undefined): exit_problems_are_manageable && hasConflictObject
	// - green (true): exit_problems_are_manageable && !hasConflictObject
	// - red (false): !exit_problems_are_manageable
	const hasConflictObject = goal.results.required_coordinations.some(
		(item) => typeof item === "object",
	);
	const exitStatusColor =
		goal.results.exit_problems_are_manageable && hasConflictObject
			? undefined // yellow
			: goal.results.exit_problems_are_manageable; // green or red

	return (
		(goal.requestedValue === requestParameter || isAP2) && (
			<React.Fragment key={index}>
				{/* TCT row */}
				<tr>
					<td className="text-xs" colSpan={2}>
						{getStatusColor(goal.results.traffic_complexity_manageable)}{" "}
						{"< 2 "}
						TCT {goal.requestedValue}
					</td>
				</tr>

				{/* FMP row */}
				<tr>
					<td className="pr-1" colSpan={2}>
						{getStatusColor(fmpIsOk)} FMP {goal.requestedValue}
					</td>
				</tr>
				{!fmpIsOk && (
					<>
						<tr>
							<td className="text-xs" colSpan={2}>
								{getStatusColor(goal.results.next_sector_capacity_ok)}{" "}
								{goal.nextSector}
							</td>
						</tr>
						<tr>
							<td className="text-xs" colSpan={2}>
								{getStatusColor(!goal.results.altitude_restriction)} LOA
							</td>
						</tr>
					</>
				)}

				{/* Exit status rows */}
				<tr>
					<td className="text-xs" colSpan={2}>
						{getStatusColor(exitStatusColor)} {goal.nextSector} MTCD{" "}
						{goal.requestedValue}
					</td>
				</tr>
				<tr>
					<td className="text-xs" colSpan={2}>
						{/* It is a coordination, if it is false, should show yellow, not red */}
						{getStatusColor(
							(goal.results.is_conform_to_flight_plan ?? true) ? true : null,
						)}{" "}
						FPL {goal.requestedValue}
					</td>
				</tr>

				{/* Separator between goals in AP2 mode */}
				{isAP2 && index < totalGoals - 1 && (
					<tr>
						<td colSpan={2}>
							<hr className="border-t border-white/30 mr-2 ml-0" />
						</td>
					</tr>
				)}
			</React.Fragment>
		)
	);
}

/** Heading goal detail row: TCT status based on in-sector conflicts. */
export function HeadingGoalRow({
	goal,
	index,
	totalGoals,
	isAP2,
	requestParameter,
}: {
	goal: NormalizedGoal;
	index: number;
	totalGoals: number;
	isAP2: boolean;
	requestParameter: number | string;
}) {
	const noConflicts = goal.inSectorConflicts?.length === 0;
	return (
		(goal.requestedValue === requestParameter || isAP2) && (
			<React.Fragment key={index}>
				<tr>
					<td className="text-xs" colSpan={2}>
						{getStatusColor(noConflicts)} TCT {goal.requestedValue}
					</td>
				</tr>
				<tr>
					{goal.nextSector && (
						<td className="text-xs" colSpan={2}>
							{getStatusColor()} COOR {goal.nextSector}
						</td>
					)}
				</tr>
				{isAP2 && index < totalGoals - 1 && (
					<tr>
						<td colSpan={2}>
							<hr className="border-t border-white/30 mr-2 ml-0" />
						</td>
					</tr>
				)}
			</React.Fragment>
		)
	);
}

/**
 * Direct-to goal detail row.
 * Shows the candidate waypoint, availability status, and next sector.
 * In AP1 mode only the goal matching the requested waypoint is shown;
 * in AP2 mode all goals are shown.
 */
export function DirectGoalRow({
	goal,
	index,
	totalGoals,
	isAP2,
	requestParameter,
}: {
	goal: NormalizedGoal;
	index: number;
	totalGoals: number;
	isAP2: boolean;
	requestParameter: number | string;
}) {
	// In AP1, show only the goal matching the requested waypoint;
	// in AP2, show all goals (so the controller can see alternatives)
	if (!isAP2 && goal.directWaypointName !== String(requestParameter)) {
		return null;
	}

	// Green = no conflicts, red = has conflicts, yellow = unknown (undefined)
	const noInSectorConflicts =
		goal.inSectorConflicts === undefined
			? null
			: goal.inSectorConflicts.length === 0;
	const noExitConflicts =
		goal.directExitConflicts === undefined
			? null
			: goal.directExitConflicts.length === 0;

	return (
		<React.Fragment key={index}>
			{/* TCT row — in-sector conflicts */}
			<tr>
				<td className="text-xs" colSpan={2}>
					{getStatusColor(noInSectorConflicts)} TCT {goal.directWaypointName}
				</td>
			</tr>

			{/* MTCD row — exit conflicts */}
			<tr>
				<td className="text-xs" colSpan={2}>
					{getStatusColor(noExitConflicts)} {goal.nextSector} MTCD{" "}
					{goal.directWaypointName}
				</td>
			</tr>

			{isAP2 && index < totalGoals - 1 && (
				<tr>
					<td colSpan={2}>
						<hr className="border-t border-white/30 mr-2 ml-0" />
					</td>
				</tr>
			)}
		</React.Fragment>
	);
}
